'use client';

import React, { useEffect, useRef } from 'react';

const bentoItems = [
  {
    id: 'stat-loads',
    className: 'col-span-2 md:col-span-1 row-span-1',
    content: (
      <div className="feature-card bg-secondary text-white rounded-xl border border-white/5 p-8 h-full flex flex-col justify-between min-h-[180px]">
        <p className="text-5xl font-800 tracking-tight text-white">1,200+</p>
        <div>
          <p className="text-white/40 text-xs font-700 uppercase tracking-widest">Loads Completed</p>
          <p className="text-white/60 text-sm font-400 mt-1">Serving our campus since day one</p>
        </div>
      </div>
    ),
  },
  {
    id: 'feature-fast',
    className: 'col-span-2 md:col-span-1 row-span-1',
    content: (
      <div className="feature-card bg-primary text-white rounded-xl p-8 h-full flex flex-col justify-between min-h-[180px] relative overflow-hidden">
        <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-white/10" />
        <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mb-4">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
          </svg>
        </div>
        <div>
          <p className="text-white font-700 text-lg leading-snug">24-Hour Turnaround</p>
          <p className="text-white/60 text-sm font-400 mt-1">Pickup to delivery in under a day</p>
        </div>
      </div>
    ),
  },
  {
    id: 'feature-track',
    className: 'col-span-2 md:col-span-2 row-span-1',
    content: (
      <div className="feature-card bg-white rounded-xl border border-border p-8 h-full shadow-card">
        <div className="flex items-start justify-between gap-6">
          <div className="flex-1">
            <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#38BDF8" strokeWidth="2.5" strokeLinecap="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </div>
            <h4 className="font-700 text-secondary text-lg mb-2">Real-Time Order Tracking</h4>
            <p className="text-muted text-sm font-400 leading-relaxed">
              Know exactly where your clothes are — picked up, being washed, dried, folded, or on the way back to your room.
            </p>
          </div>
          {/* Mini tracker UI */}
          <div className="hidden sm:block flex-shrink-0 w-[140px]">
            <div className="bg-muted-light rounded-xl p-4 space-y-3">
              {['Picked Up', 'Washing', 'Folding', 'Delivered'].map((s, i) => (
                <div key={s} className="flex items-center gap-2.5">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${i < 3 ? 'bg-primary' : 'bg-muted-light border-2 border-border'}`}>
                    {i < 3 && (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    )}
                  </div>
                  <span className={`text-[11px] font-600 ${i < 3 ? 'text-secondary' : 'text-muted'}`}>{s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'stat-time',
    className: 'col-span-2 md:col-span-1 row-span-1',
    content: (
      <div className="feature-card bg-warning/10 rounded-xl border border-warning/20 p-8 h-full flex flex-col justify-between min-h-[180px]">
        <div className="w-12 h-12 rounded-xl bg-warning/20 flex items-center justify-center">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2.5" strokeLinecap="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        </div>
        <div>
          <p className="text-4xl font-800 text-secondary tracking-tight">156 hrs</p>
          <p className="text-muted text-xs font-700 uppercase tracking-widest mt-1">Avg. time saved per semester</p>
        </div>
      </div>
    ),
  },
  {
    id: 'feature-eco',
    className: 'col-span-2 md:col-span-1 row-span-1',
    content: (
      <div className="feature-card bg-success/8 rounded-xl border border-success/20 p-8 h-full min-h-[180px]">
        <div className="w-12 h-12 rounded-xl bg-success/15 flex items-center justify-center mb-4">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="round">
            <path d="M2 22l10-10M16 8c0 4.97-3.58 9-8 9" />
            <path d="M22 2s-5 0-9 4-5 9-5 9" />
          </svg>
        </div>
        <h4 className="font-700 text-secondary text-base mb-2">Eco-Friendly Detergents</h4>
        <p className="text-muted text-sm font-400 leading-relaxed">
          We use plant-based, biodegradable detergents. Good for your clothes and the planet.
        </p>
      </div>
    ),
  },
  {
    id: 'feature-notify',
    className: 'col-span-2 md:col-span-2 row-span-1',
    content: (
      <div className="feature-card bg-white rounded-xl border border-border p-8 h-full shadow-card">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <div className="flex-1">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1A56DB" strokeWidth="2.5" strokeLinecap="round">
                <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 01-3.46 0" />
              </svg>
            </div>
            <h4 className="font-700 text-secondary text-lg mb-2">Instant Delivery Notifications</h4>
            <p className="text-muted text-sm font-400 leading-relaxed">
              Get an email notification the moment your laundry is delivered to your room. No need to keep checking — we will let you know.
            </p>
          </div>
          {/* Notification preview */}
          <div className="flex-shrink-0 w-full sm:w-auto">
            <div className="bg-muted-light rounded-xl p-5 space-y-3 min-w-[200px]">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xs">📧</span>
                </div>
                <div>
                  <p className="text-xs font-700 text-secondary">CampusWash</p>
                  <p className="text-xs text-muted">Your laundry is delivered!</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-success" />
                <span className="text-xs font-600 text-success">Delivered to Room A-204</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
  },
];

export default function FeaturesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('hidden-before-reveal');
          }
        });
      },
      { threshold: 0.1 }
    );

    const els = sectionRef.current?.querySelectorAll('.reveal, .reveal-scale');
    els?.forEach((el, i) => {
      el.classList.add('hidden-before-reveal');
      (el as HTMLElement).style.transitionDelay = `${i * 80}ms`;
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section id="features" ref={sectionRef} className="py-32 bg-white overflow-hidden">
      <div className="max-w-[1280px] mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-20 reveal">
          <div className="inline-flex items-center gap-2 bg-primary/8 rounded-full px-4 py-1.5 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
            <span className="text-primary text-xs font-700 uppercase tracking-wider">Why CampusWash</span>
          </div>
          <h2 className="font-display font-800 text-[clamp(36px,5vw,60px)] tracking-tight text-secondary leading-tight mb-4">
            Built exclusively for<br />
            <span className="text-primary">our campus</span>
          </h2>
          <p className="text-muted text-lg font-400 max-w-xl mx-auto">
            Every feature designed around hostel life — managed and operated by the college administration.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 auto-rows-min">
          {bentoItems.map((item, i) => (
            <div key={item.id} className={`reveal-scale ${item.className}`} style={{ transitionDelay: `${i * 80}ms` }}>
              {item.content}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}